// Genuine ESM, shared between src/App.jsx (Vite/browser, native import) and functions/index.js
// (Node/Cloud Functions, CommonJS, loaded via dynamic import()) — same interop pattern as
// shared/practiceGroupMatching.mjs (see that file's header for why this must be .mjs rather than
// .cjs: Vite's dev server does not apply CJS interop to local files, only `vite build` does, so a
// CommonJS module here would break `npm run dev` even though it'd work in production builds).
//
// Single source of truth for the Uniform Number Report's jersey-availability computation. Before
// this module existed, the "which jersey numbers are taken/conflicting/N-A" logic was hand-
// duplicated inline in UniformReportModal (src/App.jsx, the admin-facing report) and, separately,
// in src/UniformReport.jsx (the public /warriors/uniforms page, computed client-side from whatever
// the getUniformReport Cloud Function handed it). Consolidating here ahead of node-scoping so the
// two copies can't drift further as node-aware logic is layered on top.

// Retired/restricted numbers are HYBRID: the top-level retired/restricted arrays on
// warriors/uniformSettings are an org-wide floor that always applies to every node. Each node also
// gets its own additional restricted/retired numbers under perNode[nodeId], which apply only to
// that node's own report — a North Bay admin's additions never affect root's report and vice versa.
// A null/undefined nodeId returns the global-only set (used by the public report's default,
// no-?node=-param response, and by admins with no node context).
export function getUnavailableForNode(uniformSettings, nodeId) {
  const us = uniformSettings || {};
  const perNode = (nodeId && us.perNode && us.perNode[nodeId]) || {};
  return [
    ...(us.retired || []),
    ...(us.restricted || []),
    ...(perNode.retired || []),
    ...(perNode.restricted || []),
  ];
}

// Active + has a jersey number assigned — the filter both render paths have always applied before
// building the jersey x age-group grid (players without a jersey yet, or inactive/graduated
// players, never occupy a slot in the report).
export function filterActiveRosteredPlayers(players) {
  return (players || []).filter((p) => (p.status || "active") === "active" && p.jersey);
}

// Full availability-table computation for one node's report. `players` should already be scoped to
// the node being reported on (callers filter by nodeId themselves before calling this, since one is
// client-side live state and the other is a Firestore query — this function only needs to know
// which node's retired/restricted floor+additions apply). Returns the pieces a render loop needs
// directly: activePlayers (for header counts), allNums (1-99 plus any configured numbers outside
// that range), isUnavailable/getCell as lookup functions (matching how both existing render loops
// already call per-row/per-cell logic rather than a fully materialized grid), and conflictCount
// (numbers with 2+ players in the same age group, excluding N/A rows).
export function computeUniformAvailability({players, uniformSettings, nodeId, ageGroups}) {
  const activePlayers = filterActiveRosteredPlayers(players);
  const getCell = (jersey, ageGroup) => activePlayers.filter((p) => p.jersey === jersey && p.ageGroup === ageGroup);
  const unavailableNums = getUnavailableForNode(uniformSettings, nodeId);
  const isUnavailable = (n) => unavailableNums.includes(n);
  const base = Array.from({length: 99}, (_, i) => String(i + 1));
  const extras = unavailableNums.filter((n) => !base.includes(n));
  const allNums = [...new Set([...extras, ...base])].sort((a, b) => Number(a) - Number(b) || a.localeCompare(b));
  const conflictCount = allNums.filter((j) => !isUnavailable(j) && (ageGroups || []).some((ag) => getCell(j, ag).length > 1)).length;
  return {activePlayers, allNums, isUnavailable, getCell, conflictCount};
}
